// src/components/CoachesHub.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../utils/firebase';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { Calendar, Users, MapPin, Clock, Menu as MenuIcon, X } from 'lucide-react';
import logo from '../assets/images/logo2.PNG';

const CoachesHub = () => {
  const navigate = useNavigate();
  
  // State
  const [user, setUser] = useState(null);
  const [clubId, setClubId] = useState(null);
  const [clubName, setClubName] = useState('');
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Mobile menu
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Team selection
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('');
  
  // Date range
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    const twoWeeks = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
    return `${twoWeeks.getFullYear()}-${String(twoWeeks.getMonth() + 1).padStart(2, '0')}-${String(twoWeeks.getDate()).padStart(2, '0')}`;
  });
  
  // Pitch data
  const [pitches, setPitches] = useState([]);
  const [pitchNames, setPitchNames] = useState({});
  
  // Sessions data
  const [sessions, setSessions] = useState([]);
  
  // Multi-facility support
  const [facilities, setFacilities] = useState([]);
  const [facilityPitchMap, setFacilityPitchMap] = useState({});

  // Auth effect
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate('/login');
        return;
      }
      setUser(currentUser);
      
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setClubId(userData.clubId);
          
          // Load club data
          const clubDocRef = doc(db, 'clubs', userData.clubId);
          const clubDoc = await getDoc(clubDocRef);
          
          if (clubDoc.exists()) {
            const clubData = clubDoc.data();
            setClubName(clubData.name || 'Your Club');
            
            if (clubData.branding) {
              setBranding(clubData.branding);
            }
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    });
    
    return () => unsubscribe();
  }, [navigate]);

  // Load teams, pitches, and facilities
  useEffect(() => {
    if (!clubId) {
      console.log('⏳ Waiting for clubId...');
      return;
    }
    
    console.log('🚀 Loading club data for clubId:', clubId);
    
    const loadClubData = async () => {
      try {
        // Load club document first to get teams
        const clubDocRef = doc(db, 'clubs', clubId);
        const clubDoc = await getDoc(clubDocRef);
        
        let teamsList = [];
        
        if (clubDoc.exists()) {
          const clubData = clubDoc.data();
          console.log('📋 Club data:', clubData);
          
          // Try to get teams from club document
          if (clubData.teams) {
            console.log('📊 Found teams in club document:', clubData.teams);
            
            // Handle both array and object formats
            if (Array.isArray(clubData.teams)) {
              teamsList = clubData.teams.map((team, idx) => ({
                id: team.id || `team-${idx}`,
                name: team.name || team.teamName || `Team ${idx + 1}`,
                ...team
              }));
            } else if (typeof clubData.teams === 'object') {
              teamsList = Object.entries(clubData.teams).map(([key, team]) => ({
                id: key,
                name: typeof team === 'string' ? team : (team.name || team.teamName || key),
                ...team
              }));
            }
          }
        }
        
        // Load settings document (we'll need it for teams and pitches)
        let settingsDoc = null;
        const settingsRef = doc(db, 'clubs', clubId, 'settings', 'general');
        settingsDoc = await getDoc(settingsRef);
        
        // If no teams in club document, try settings
        if (teamsList.length === 0) {
          console.log('📋 No teams in club document, checking settings...');
          
          if (settingsDoc.exists()) {
            const settings = settingsDoc.data();
            console.log('⚙️ Settings data:', settings);
            
            if (settings.teams) {
              console.log('📊 Found teams in settings:', settings.teams);
              
              // Handle both array and object formats
              if (Array.isArray(settings.teams)) {
                teamsList = settings.teams.map((team, idx) => ({
                  id: team.id || `team-${idx}`,
                  name: team.name || team.teamName || `Team ${idx + 1}`,
                  ...team
                }));
              } else if (typeof settings.teams === 'object') {
                teamsList = Object.entries(settings.teams).map(([key, team]) => ({
                  id: key,
                  name: typeof team === 'string' ? team : (team.name || team.teamName || key),
                  ...team
                }));
              }
            }
            
            // Also load pitch names from settings
            if (settings.pitchNames) {
              setPitchNames(settings.pitchNames);
            }
          }
        }
        
        console.log('📋 Teams before sorting:', teamsList);
        
        // Sort by name
        const sortedTeams = teamsList.sort((a, b) => {
          const nameA = a.name || '';
          const nameB = b.name || '';
          return nameA.localeCompare(nameB);
        });
        
        console.log('✅ Sorted teams:', sortedTeams);
        setTeams(sortedTeams);
        
        if (sortedTeams.length === 0) {
          console.warn('⚠️ No teams found! Teams should be in club document or settings.');
        }
        
        // Load pitches - check club document's satelliteConfig first
        console.log('📊 Loading pitches...');
        let pitchesList = [];
        
        // Method 1: Load from club document's satelliteConfig.pitchBoundaries (like CapacityOutlook)
        if (clubDoc && clubDoc.exists()) {
          const clubData = clubDoc.data();
          console.log('🔍 Checking club document for satelliteConfig...');
          
          if (clubData.satelliteConfig?.pitchBoundaries && clubData.satelliteConfig.pitchBoundaries.length > 0) {
            console.log('✅ Found satelliteConfig.pitchBoundaries in club document:', clubData.satelliteConfig.pitchBoundaries);
            
            pitchesList = clubData.satelliteConfig.pitchBoundaries.map((boundary, index) => {
              console.log(`  🏟️ Processing boundary ${index}:`, boundary);
              
              // Extract pitch number from id field
              let pitchNum = boundary.pitchNumber || boundary.id || `${index + 1}`;
              
              // If pitchNum contains "pitch-", extract just the number
              if (typeof pitchNum === 'string' && pitchNum.includes('pitch-')) {
                pitchNum = pitchNum.replace('pitch-', '');
              }
              
              console.log(`    - Pitch number: ${pitchNum}`);
              
              return {
                id: pitchNum,
                pitchNumber: pitchNum,
                name: boundary.name || `Pitch ${pitchNum}`,
                ...boundary
              };
            });
            
            console.log('✅ Loaded pitches from club satelliteConfig:', pitchesList);
          } else {
            console.warn('⚠️ No satelliteConfig.pitchBoundaries in club document');
            console.log('   satelliteConfig exists:', !!clubData.satelliteConfig);
            console.log('   pitchBoundaries exists:', !!clubData.satelliteConfig?.pitchBoundaries);
          }
        }
        
        // Method 2: Try settings as fallback
        if (pitchesList.length === 0) {
          console.log('📊 No pitches in club document, checking settings...');
          const settingsRefForPitches = doc(db, 'clubs', clubId, 'settings', 'general');
          const settingsSnapshotForPitches = await getDoc(settingsRefForPitches);
          
          if (settingsSnapshotForPitches.exists()) {
            const settings = settingsSnapshotForPitches.data();
            
            if (settings.satelliteConfig?.pitchBoundaries) {
              console.log('📊 Found pitchBoundaries in settings:', settings.satelliteConfig.pitchBoundaries);
              pitchesList = settings.satelliteConfig.pitchBoundaries.map((pitch, index) => ({
                id: pitch.pitchNumber || pitch.id || `${index + 1}`,
                name: pitch.name,
                pitchNumber: pitch.pitchNumber,
                ...pitch
              }));
            }
          }
        }
        
        // Method 3: Try pitches collection as last resort
        if (pitchesList.length === 0) {
          console.log('📊 No pitches in settings, trying pitches collection...');
          const pitchesRef = collection(db, 'clubs', clubId, 'pitches');
          const pitchesSnapshot = await getDocs(pitchesRef);
          pitchesList = pitchesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log('📊 Found pitches in collection:', pitchesList);
        }
        
        setPitches(pitchesList);
        console.log('✅ Loaded pitches:', pitchesList);
        
        if (pitchesList.length === 0) {
          console.error('❌ NO PITCHES FOUND in any source!');
          console.error('   Pitches should be in either:');
          console.error('   1. clubData.satelliteConfig.pitchBoundaries (club document)');
          console.error('   2. settings.satelliteConfig.pitchBoundaries');
          console.error('   3. clubs/{clubId}/pitches collection');
          console.warn('⚠️ FALLBACK: Using default pitch-1 since allocations exist in database');
          
          // Fallback: We know from the database that pitch-1 exists with allocations
          // This ensures the component works even if pitch configuration is missing
          pitchesList = [{ id: '1', pitchNumber: 1, name: 'Pitch 1' }];
          setPitches(pitchesList);
          console.log('✅ Using fallback pitches:', pitchesList);
        }
        
        // Load facilities
        const facilitiesRef = collection(db, 'clubs', clubId, 'facilities');
        const facilitiesSnapshot = await getDocs(facilitiesRef);
        
        if (!facilitiesSnapshot.empty) {
          const facilitiesList = facilitiesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setFacilities(facilitiesList);
          
          // Build facility-pitch mapping
          const pitchMap = {};
          facilitiesList.forEach(facility => {
            if (facility.pitches && Array.isArray(facility.pitches)) {
              facility.pitches.forEach(pitchId => {
                pitchMap[pitchId] = facility.id;
              });
            }
          });
          setFacilityPitchMap(pitchMap);
          console.log('Loaded facilities:', facilitiesList);
          console.log('Facility-pitch map:', pitchMap);
        }
        
      } catch (error) {
        console.error('Error loading club data:', error);
      }
    };
    
    loadClubData();
  }, [clubId]);

  // Load sessions when team or date range changes
  useEffect(() => {
    if (!clubId || !selectedTeam || !startDate || !endDate) return;
    
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId, selectedTeam, startDate, endDate]);

  const loadSessions = async () => {
    if (!selectedTeam) {
      console.log('❌ No team selected');
      return;
    }
    
    setLoading(true);
    
    try {
      console.log('🔍 ========== LOADING SESSIONS ==========');
      console.log('🔍 Selected team:', selectedTeam);
      console.log('🔍 Date range:', startDate, 'to', endDate);
      console.log('🔍 Club ID:', clubId);
      console.log('🔍 Pitches state:', pitches);
      console.log('🔍 Number of pitches:', pitches.length);
      
      if (pitches.length === 0) {
        console.error('❌ CRITICAL: No pitches loaded! Cannot query allocations.');
        console.error('❌ This means the pitches collection is empty or failed to load.');
        setSessions([]);
        setLoading(false);
        return;
      }
      
      const allAllocations = [];
      
      // Query each pitch's allocations
      for (const pitch of pitches) {
        console.log('🔍 Processing pitch object:', pitch);
        const pitchNum = String(pitch.id).replace(/\D/g, '');
        console.log('🔍 Extracted pitch number:', pitchNum);
        
        const allocPath = `clubs/${clubId}/allocations/pitch-${pitchNum}/items`;
        console.log(`📊 Querying path: ${allocPath}`);
        
        try {
          const allocationsRef = collection(db, 'clubs', clubId, 'allocations', `pitch-${pitchNum}`, 'items');
          const allocationsSnapshot = await getDocs(allocationsRef);
          
          console.log(`📊 Pitch-${pitchNum}: found ${allocationsSnapshot.size} total documents`);
          
          if (allocationsSnapshot.size === 0) {
            console.warn(`⚠️ No allocations found for pitch-${pitchNum}`);
            continue;
          }
          
          let matchCount = 0;
          allocationsSnapshot.forEach((docSnap) => {
            const docId = docSnap.id;
            const data = docSnap.data();
            
            // Parse document ID: YYYY-MM-DD-HH:MM-pitch-X-Section
            const parts = docId.split('-');
            if (parts.length >= 7) {
              const docDate = `${parts[0]}-${parts[1]}-${parts[2]}`;
              const timeToken = parts[3];
              const section = parts[6];
              
              // Check if date is in range
              const inDateRange = docDate >= startDate && docDate <= endDate;
              
              if (!inDateRange) {
                console.log(`  ⏭️ Skip ${docId}: date ${docDate} not in range ${startDate} to ${endDate}`);
                return;
              }
              
              // Check if team matches
              const teamName = data.teamName || data.team || '';
              const teamId = data.teamId || '';
              
              console.log(`  🏷️ Doc ${docId}:`);
              console.log(`     Date: ${docDate} (in range: ${inDateRange})`);
              console.log(`     Team in data: "${teamName}" (ID: "${teamId}")`);
              console.log(`     Selected team: "${selectedTeam}"`);
              console.log(`     Match by name: ${teamName === selectedTeam}`);
              console.log(`     Match by ID: ${teamId === selectedTeam}`);
              console.log(`     Data:`, data);
              
              // Match by team name OR team ID
              if (teamName === selectedTeam || teamId === selectedTeam) {
                matchCount++;
                console.log(`  ✅ MATCH #${matchCount}! Adding to results`);
                allAllocations.push({
                  docId,
                  date: docDate,
                  time: timeToken,
                  pitchId: `pitch-${pitchNum}`,
                  section,
                  teamName,
                  teamId,
                  startTime: data.startTime || timeToken,
                  endTime: data.endTime || '',
                  allocationType: data.allocationType || data.type || 'unknown',
                  ...data
                });
              } else {
                console.log(`  ❌ No match - team name mismatch`);
              }
            } else {
              console.warn(`  ⚠️ Invalid docId format: ${docId} (${parts.length} parts)`);
            }
          });
          
          console.log(`📊 Pitch-${pitchNum} summary: ${matchCount} matches found`);
        } catch (pitchError) {
          console.error(`❌ Error querying pitch-${pitchNum}:`, pitchError);
        }
      }
      
      console.log('📦 ========== QUERY COMPLETE ==========');
      console.log('📦 Total allocations found:', allAllocations.length);
      
      if (allAllocations.length === 0) {
        console.warn('⚠️ NO ALLOCATIONS FOUND!');
        console.warn('⚠️ Possible reasons:');
        console.warn('   1. Team name in dropdown does not match team name in allocation data');
        console.warn('   2. No allocations exist in the selected date range');
        console.warn('   3. Allocations exist but pitch IDs do not match');
        console.log('🔍 Debug info:');
        console.log('   Selected team value:', `"${selectedTeam}"`);
        console.log('   Date range:', startDate, 'to', endDate);
        console.log('   Today is:', new Date().toISOString().split('T')[0]);
        console.log('   Pitches queried:', pitches.map(p => `pitch-${String(p.id).replace(/\D/g, '')}`));
      } else {
        console.log('📋 Sample allocation:', allAllocations[0]);
      }
      
      // Consolidate consecutive time slots
      const consolidated = consolidateAllocations(allAllocations);
      
      console.log('✨ ========== CONSOLIDATION COMPLETE ==========');
      console.log('✨ Consolidated sessions:', consolidated.length);
      if (consolidated.length > 0) {
        console.log('✨ Sample session:', consolidated[0]);
      }
      
      setSessions(consolidated);
      
    } catch (error) {
      console.error('❌ ========== ERROR ==========');
      console.error('❌ Error loading sessions:', error);
      console.error('❌ Error stack:', error.stack);
    } finally {
      setLoading(false);
    }
  };

  const consolidateAllocations = (allocations) => {
    if (allocations.length === 0) return [];
    
    console.log('🔧 Starting consolidation with', allocations.length, 'allocations');
    
    // Group by date, pitch, and time (not by section!)
    const grouped = {};
    
    allocations.forEach(allocation => {
      const allocStartTime = allocation.startTime || allocation.time;
      const key = `${allocation.date}_${allocation.pitchId}_${allocStartTime}`;
      
      if (!grouped[key]) {
        grouped[key] = {
          date: allocation.date,
          pitchId: allocation.pitchId,
          startTime: allocStartTime,
          endTime: allocation.endTime || addMinutes(allocStartTime, 15),
          sections: new Set(),
          teamName: allocation.teamName,
          allocationType: allocation.allocationType || allocation.type,
          allocations: []
        };
      }
      
      // Add this section to the group
      grouped[key].sections.add(allocation.section);
      grouped[key].allocations.push(allocation);
      
      // Update end time if this allocation has a later end time
      if (allocation.endTime) {
        const currentEndMinutes = timeToMinutes(grouped[key].endTime);
        const thisEndMinutes = timeToMinutes(allocation.endTime);
        if (thisEndMinutes > currentEndMinutes) {
          grouped[key].endTime = allocation.endTime;
        }
      }
    });
    
    console.log('📊 Grouped into', Object.keys(grouped).length, 'time slots');
    
    // Convert to array and look for consecutive time blocks
    const timeSlots = Object.values(grouped);
    
    // Sort by date, pitch, then start time
    timeSlots.sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      if (a.pitchId !== b.pitchId) return a.pitchId.localeCompare(b.pitchId);
      return a.startTime.localeCompare(b.startTime);
    });
    
    const sessions = [];
    let currentSession = null;
    
    timeSlots.forEach(slot => {
      if (!currentSession) {
        // Start new session
        currentSession = {
          date: slot.date,
          pitchId: slot.pitchId,
          startTime: slot.startTime,
          endTime: slot.endTime,
          sections: new Set(slot.sections),
          teamName: slot.teamName,
          allocationType: slot.allocationType
        };
        console.log('  🆕 Started new session:', currentSession.startTime, 'sections:', Array.from(currentSession.sections));
      } else {
        // Check if this slot continues the current session
        const isSameDateAndPitch = 
          currentSession.date === slot.date && 
          currentSession.pitchId === slot.pitchId;
        
        const currentEndTime = currentSession.endTime;
        const slotStartTime = slot.startTime;
        
        // Check if times are consecutive (within 15 minutes)
        const timeDiff = Math.abs(timeToMinutes(slotStartTime) - timeToMinutes(currentEndTime));
        const isConsecutive = timeDiff <= 15;
        
        // Check if sections overlap (they should be the same sections continuing)
        const sectionsMatch = Array.from(slot.sections).some(s => currentSession.sections.has(s));
        
        console.log(`    🔗 Comparing slots:`);
        console.log(`       Current: ${currentSession.startTime}-${currentSession.endTime}, sections: ${Array.from(currentSession.sections)}`);
        console.log(`       Next: ${slot.startTime}-${slot.endTime}, sections: ${Array.from(slot.sections)}`);
        console.log(`       Same date/pitch: ${isSameDateAndPitch}, Consecutive: ${isConsecutive}, Sections match: ${sectionsMatch}`);
        
        if (isSameDateAndPitch && isConsecutive && sectionsMatch) {
          // Extend the current session
          currentSession.endTime = slot.endTime;
          slot.sections.forEach(s => currentSession.sections.add(s));
          console.log(`    ✅ Extended session to: ${currentSession.endTime}, sections: ${Array.from(currentSession.sections)}`);
        } else {
          // Save current session and start new one
          console.log('    ⏭️ Saving session and starting new one');
          sessions.push({
            ...currentSession,
            sections: Array.from(currentSession.sections).sort()
          });
          
          currentSession = {
            date: slot.date,
            pitchId: slot.pitchId,
            startTime: slot.startTime,
            endTime: slot.endTime,
            sections: new Set(slot.sections),
            teamName: slot.teamName,
            allocationType: slot.allocationType
          };
          console.log('  🆕 Started new session:', currentSession.startTime, 'sections:', Array.from(currentSession.sections));
        }
      }
    });
    
    // Don't forget the last session
    if (currentSession) {
      sessions.push({
        ...currentSession,
        sections: Array.from(currentSession.sections).sort()
      });
      console.log('  💾 Saved final session');
    }
    
    console.log('✅ Consolidation complete:', sessions.length, 'sessions');
    if (sessions.length > 0) {
      console.log('   Sample:', sessions[0]);
    }
    
    return sessions;
  };

  const timeToMinutes = (time) => {
    const [hours, mins] = time.split(':').map(Number);
    return hours * 60 + mins;
  };

  const addMinutes = (time, minutes) => {
    const [hours, mins] = time.split(':').map(Number);
    const totalMins = hours * 60 + mins + minutes;
    const newHours = Math.floor(totalMins / 60);
    const newMins = totalMins % 60;
    return `${String(newHours).padStart(2, '0')}:${String(newMins).padStart(2, '0')}`;
  };

  const getPitchDisplayName = (pitchId) => {
    const pitchNum = String(pitchId).replace(/\D/g, '');
    const possibleKeys = [
      `pitch-${pitchNum}`,
      `Pitch ${pitchNum}`,
      `pitch${pitchNum}`,
      pitchNum,
    ];
    
    for (const key of possibleKeys) {
      if (pitchNames && pitchNames[key] && pitchNames[key].trim()) {
        return pitchNames[key];
      }
    }
    
    return `Pitch ${pitchNum}`;
  };

  const getFacilityName = (pitchId) => {
    const facilityId = facilityPitchMap[pitchId];
    if (!facilityId) return null;
    
    const facility = facilities.find(f => f.id === facilityId);
    return facility ? facility.name : null;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-AU', { 
      weekday: 'short',
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatTime = (time) => {
    const [hours, mins] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'pm' : 'am';
    const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
    return `${displayHour}:${mins}${ampm}`;
  };

  const getSessionDuration = (startTime, endTime) => {
    const [startHours, startMins] = startTime.split(':').map(Number);
    const [endHours, endMins] = endTime.split(':').map(Number);
    const totalMins = (endHours * 60 + endMins) - (startHours * 60 + startMins);
    
    if (totalMins >= 60) {
      const hours = Math.floor(totalMins / 60);
      const mins = totalMins % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${totalMins}m`;
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div style={{ 
      display: 'flex',
      minHeight: '100vh', 
      backgroundColor: '#1a2942',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Desktop Sidebar */}
      <div 
        className="desktop-sidebar"
        style={{
          width: '250px',
          flexShrink: 0,
          backgroundColor: '#243665',
          display: 'flex',
          flexDirection: 'column',
          position: 'sticky',
          top: 0,
          height: '100vh'
        }}
      >
        {/* User Info Card */}
        <div style={{ padding: '12px' }}>
          <div style={{
            background: `linear-gradient(135deg, ${branding?.primaryColor || '#10b981'} 0%, ${branding?.secondaryColor || '#3b82f6'} 100%)`,
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '6px'
            }}>
              {branding?.clubLogoUrl && (
                <img 
                  src={branding.clubLogoUrl} 
                  alt="Club Logo" 
                  style={{
                    height: '40px',
                    width: '40px',
                    objectFit: 'contain',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }}
                />
              )}
              <div style={{
                fontSize: '17px',
                fontWeight: '600',
                color: 'white',
                lineHeight: '1.3'
              }}>
                {clubName}
              </div>
            </div>
            <div style={{
              fontSize: '13px',
              color: 'rgba(255,255,255,0.7)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {user?.email}
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div style={{ 
          flex: 1,
          padding: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          <button
            onClick={() => navigate('/menu')}
            style={{
              width: '100%',
              padding: '10px 12px',
              backgroundColor: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'rgba(255,255,255,0.9)',
              cursor: 'pointer',
              fontWeight: '500',
              textAlign: 'left',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
          >
            Home Menu
          </button>
          
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: '10px 12px',
              backgroundColor: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'rgba(255,255,255,0.9)',
              cursor: 'pointer',
              fontWeight: '500',
              textAlign: 'left',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#1a2942'
      }}>
        {/* Mobile Header */}
        <div 
          className="mobile-header"
          style={{
            display: 'none',
            backgroundColor: '#243665',
            padding: '16px',
            alignItems: 'center',
            gap: '16px',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            {isMobileMenuOpen ? <X size={24} /> : <MenuIcon size={24} />}
          </button>
          
          {branding?.clubLogoUrl ? (
            <img 
              src={branding.clubLogoUrl} 
              alt={clubName}
              style={{ height: '36px', objectFit: 'contain' }}
            />
          ) : (
            <img src={logo} alt="Pitchallo" style={{ height: '36px' }} />
          )}
          
          <div style={{ flex: 1 }}>
            <h1 style={{ 
              margin: 0, 
              fontSize: '18px',
              fontWeight: '600',
              color: 'white'
            }}>
              Coaches Hub
            </h1>
          </div>
        </div>

        {/* Content Area */}
        <div style={{ 
          flex: 1,
          overflowY: 'auto',
          padding: '24px'
        }}>
          {/* Desktop Header */}
          <div 
            className="desktop-header"
            style={{
              marginBottom: '24px'
            }}
          >
            <h1 style={{ 
              margin: 0, 
              fontSize: '32px',
              fontWeight: 'bold',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <Users size={36} style={{ color: branding?.primaryColor || '#10b981' }} />
              Coaches Hub
            </h1>
            <p style={{
              margin: '8px 0 0 0',
              fontSize: '16px',
              color: 'rgba(255,255,255,0.7)'
            }}>
              View your team's training sessions
            </p>
          </div>

          {/* Filters Card */}
          <div style={{
            backgroundColor: '#243665',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            border: '1px solid rgba(255,255,255,0.1)'
          }}>
            <h2 style={{ 
              margin: '0 0 20px 0',
              fontSize: '18px',
              fontWeight: '600',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Users size={20} />
              Select Your Team
            </h2>
            
            <div style={{
              display: 'grid',
              gap: '16px',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))'
            }}>
              {/* Team Selection */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'rgba(255,255,255,0.9)'
                }}>
                  Team
                </label>
                <select
                  value={selectedTeam}
                  onChange={(e) => {
                    console.log('🎯 Team selected:', e.target.value);
                    setSelectedTeam(e.target.value);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="" style={{ backgroundColor: '#243665' }}>
                    Choose a team... {teams.length > 0 ? `(${teams.length} teams available)` : '(No teams found)'}
                  </option>
                  {teams.map(team => {
                    const displayName = team.name || team.teamName || team.id;
                    console.log('🏷️ Rendering team option:', displayName, team);
                    return (
                      <option key={team.id} value={team.name || team.teamName} style={{ backgroundColor: '#243665' }}>
                        {displayName}
                      </option>
                    );
                  })}
                </select>
              </div>
              
              {/* Start Date */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'rgba(255,255,255,0.9)'
                }}>
                  <Calendar size={14} style={{ display: 'inline', marginRight: '4px' }} />
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    colorScheme: 'dark'
                  }}
                />
              </div>
              
              {/* End Date */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'rgba(255,255,255,0.9)'
                }}>
                  <Calendar size={14} style={{ display: 'inline', marginRight: '4px' }} />
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    colorScheme: 'dark'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Sessions List */}
          {loading ? (
            <div style={{
              backgroundColor: '#243665',
              borderRadius: '12px',
              padding: '48px',
              textAlign: 'center',
              border: '1px solid rgba(255,255,255,0.1)'
            }}>
              <div style={{
                display: 'inline-block',
                width: '40px',
                height: '40px',
                border: '4px solid rgba(255,255,255,0.1)',
                borderTopColor: branding?.primaryColor || '#10b981',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <p style={{ marginTop: '16px', color: 'rgba(255,255,255,0.7)' }}>
                Loading sessions...
              </p>
            </div>
          ) : !selectedTeam ? (
            <div style={{
              backgroundColor: '#243665',
              borderRadius: '12px',
              padding: '48px',
              textAlign: 'center',
              border: '1px solid rgba(255,255,255,0.1)'
            }}>
              <Users size={48} style={{ color: 'rgba(255,255,255,0.3)', margin: '0 auto 16px' }} />
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '16px' }}>
                Please select a team to view sessions
              </p>
            </div>
          ) : sessions.length === 0 ? (
            <div style={{
              backgroundColor: '#243665',
              borderRadius: '12px',
              padding: '48px',
              textAlign: 'center',
              border: '1px solid rgba(255,255,255,0.1)'
            }}>
              <Calendar size={48} style={{ color: 'rgba(255,255,255,0.3)', margin: '0 auto 16px' }} />
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '16px' }}>
                No sessions found for this team in the selected date range
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {sessions.map((session, index) => {
                const pitchDisplay = getPitchDisplayName(session.pitchId);
                const facilityName = getFacilityName(session.pitchId);
                const duration = getSessionDuration(session.startTime, session.endTime);
                
                // Extract pitch number for navigation
                const pitchNumber = String(session.pitchId).replace(/\D/g, '');
                
                return (
                  <div
                    key={index}
                    onClick={() => {
                      // Navigate to UnifiedPitchAllocator with the date and pitch
                      console.log('🎯 Navigating to allocator:', {
                        pitch: pitchNumber,
                        date: session.date
                      });
                      navigate(`/allocator/${pitchNumber}?date=${session.date}`);
                    }}
                    style={{
                      backgroundColor: '#243665',
                      borderRadius: '12px',
                      padding: '24px',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderLeft: `4px solid ${branding?.primaryColor || '#10b981'}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#2d4373';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#243665';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {/* Click indicator */}
                    <div style={{
                      position: 'absolute',
                      top: '24px',
                      right: '24px',
                      fontSize: '12px',
                      color: 'rgba(255,255,255,0.5)',
                      fontWeight: '500'
                    }}>
                      Click to view →
                    </div>
                    <div style={{
                      display: 'grid',
                      gap: '20px',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))'
                    }}>
                      {/* Date */}
                      <div>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: 'rgba(255,255,255,0.5)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '6px'
                        }}>
                          Date
                        </div>
                        <div style={{
                          fontSize: '18px',
                          fontWeight: '600',
                          color: 'white'
                        }}>
                          {formatDate(session.date)}
                        </div>
                      </div>
                      
                      {/* Time */}
                      <div>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: 'rgba(255,255,255,0.5)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '6px'
                        }}>
                          <Clock size={12} style={{ display: 'inline', marginRight: '4px' }} />
                          Time
                        </div>
                        <div style={{
                          fontSize: '18px',
                          fontWeight: '600',
                          color: 'white'
                        }}>
                          {formatTime(session.startTime)} - {formatTime(session.endTime)}
                        </div>
                        <div style={{
                          fontSize: '13px',
                          color: 'rgba(255,255,255,0.6)',
                          marginTop: '4px'
                        }}>
                          {duration}
                        </div>
                      </div>
                      
                      {/* Pitch & Location */}
                      <div>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: 'rgba(255,255,255,0.5)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '6px'
                        }}>
                          <MapPin size={12} style={{ display: 'inline', marginRight: '4px' }} />
                          Location
                        </div>
                        <div style={{
                          fontSize: '18px',
                          fontWeight: '600',
                          color: 'white'
                        }}>
                          {pitchDisplay}
                        </div>
                        {facilityName && (
                          <div style={{
                            fontSize: '13px',
                            color: 'rgba(255,255,255,0.6)',
                            marginTop: '4px'
                          }}>
                            {facilityName}
                          </div>
                        )}
                      </div>
                      
                      {/* Sections */}
                      <div>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: 'rgba(255,255,255,0.5)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '6px'
                        }}>
                          Sections
                        </div>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '8px'
                        }}>
                          {session.sections.map(section => (
                            <span
                              key={section}
                              style={{
                                backgroundColor: branding?.primaryColor || '#10b981',
                                color: 'white',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: '600'
                              }}
                            >
                              {section}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .desktop-sidebar {
            display: none !important;
          }
          .mobile-header {
            display: flex !important;
          }
          .desktop-header {
            display: none !important;
          }
        }
        
        @media (min-width: 769px) {
          .mobile-header {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default CoachesHub;
